import { CampusSelect } from '@/components/common/input/campus';
import { MemberStatusSelect } from '@/components/common/input/memberstatus';
import type { Pagination } from '@/components/common/table';
import { DataTable } from '@/components/common/table';
import { useApiWithAuth } from '@/hooks/api';
import { usePeopleStore } from '@/store/people';
import type { LoadPeopleSearchParams } from '@/store/people/actions/people';
import { loadPerson, updatePerson } from '@/store/people/actions/people';
import { isPersonId, type Person } from '@/store/people/types';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { LoadingBar } from '../../common/loading';
import type { ActionKey, ColumnKey } from './columns';
import { makeColumns } from './columns';
import { PeopleSearch } from '@/components/common/input/people';
import { useDefaultCampus } from '@/store/org/hooks';
import { GroupSelect } from '@/components/common/input/group';
import { getGroupAlias, getGroupProperty } from '../structure/utils';
import { Icon } from '@iconify/react/dist/iconify.js';
import { PersonModal } from './modal';
import { useDisclosure } from '@/components/ui';
import { useLoadPeople } from './hooks';
import { HouseholdModal } from './household';
import { Debug } from '@/components/common/output/debug';
import { useMyPeopleAccess, type PeopleContext } from '@/store/access/access';
import { type MinistryTypeKey } from '@/shared/types/ministry';
import { MinistryTypeSelect } from '@/components/common/input/ministrytype';
import { useReminderModal } from '@/components/reminders/context';
import { useDrawerActions } from '@/store/drawer';
import { useCommentModal } from '@/components/comments/context';
import { useMilestoneModal } from '@/components/milestones/context';
import { useTagsModal } from '@/components/profile';
import { TagSelect } from '@/components/common/tag';

const defaultItemsPerPage = 10;
const itemLimit = 100;

interface Filters {
  campus: string;
  ministry: MinistryTypeKey | '';
  memberStatus: string[];
  group1: string;
  group2: string;
  search: LoadPeopleSearchParams | undefined;
  tags: string[];
}

type FilterUpdate =
  | { type: 'setCampus'; value: string }
  | { type: 'setMinistry'; value: MinistryTypeKey | '' }
  | { type: 'setMemberStatus'; value: string[] }
  | { type: 'setGroup1' | 'setGroup2'; value: string }
  | { type: 'setSearch'; value: LoadPeopleSearchParams | undefined }
  | { type: 'setTags'; value: string[] };

const filterUpdate = (state: Filters, params: FilterUpdate): Filters => {
  const { type, value } = params;
  switch (type) {
    case 'setCampus':
      if (state.campus === value) {
        return state;
      }

      return { ...state, campus: value };
    case 'setMinistry':
      if (state.ministry === value) {
        return state;
      }

      return { ...state, ministry: value };
    case 'setGroup1':
      if (state.group1 === value) {
        return state;
      }

      return { ...state, group1: value };
    case 'setGroup2':
      if (state.group2 === value) {
        return state;
      }

      return { ...state, group2: value };
    case 'setMemberStatus':
      if (state.memberStatus === value) {
        return state;
      }

      return { ...state, memberStatus: value };
    case 'setSearch':
      if (!state.search && !value) {
        return state;
      }

      if (
        state.search &&
        value &&
        state.search.type === value?.type &&
        state.search.value === value?.value
      ) {
        return state;
      }

      return { ...state, search: value };
    case 'setTags':
      return { ...state, tags: value || [] };
    default:
      return state;
  }
};

export function People() {
  const renderCount = useRef(0);
  renderCount.current++;
  console.log('renderCount', renderCount.current);
  const store = usePeopleStore();
  const { api, auth } = useApiWithAuth();
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
  const defaultCampus = useDefaultCampus();
  const {
    setOpen: setDrawerIsOpen,
    setCurrentPerson: setDrawerCurrentPerson,
    setCurrentTab: setDrawerCurrentTab,
  } = useDrawerActions();
  const { setOpen: setTagsModalOpen, setCurrentPerson: setTagsModalPerson } =
    useTagsModal();

  const [filters, updateFilters] = useReducer(filterUpdate, {
    campus: defaultCampus,
    ministry: '',
    group1: '',
    group2: '',
    memberStatus: [],
    search: undefined,
    tags: [],
  } satisfies Filters);

  const campus = filters.campus;
  const setCampus = (value: string) =>
    updateFilters({
      type: 'setCampus',
      value,
    });

  const ministry = filters.ministry;
  const setMinistry = (value: '' | MinistryTypeKey) =>
    updateFilters({ type: 'setMinistry', value });

  const memberStatus = filters.memberStatus;
  const setMemberStatus = (value: string[]) =>
    updateFilters({ type: 'setMemberStatus', value });

  const group1 = filters.group1;
  const setGroup1 = (value: string) =>
    updateFilters({ type: 'setGroup1', value });

  const group2 = filters.group2;
  const setGroup2 = (value: string) =>
    updateFilters({ type: 'setGroup2', value });

  const search = filters.search;
  const setSearch = useCallback(
    (value: undefined | LoadPeopleSearchParams) =>
      updateFilters({ type: 'setSearch', value }),
    [],
  );

  const tags = filters.tags;
  const setTags = (value: string[]) =>
    updateFilters({ type: 'setTags', value });

  const [page, setPage] = useState(1);
  const context = useMemo((): PeopleContext => {
    return {
      campus_id: campus || undefined,
      ministry: ministry || undefined,
      group1: group1 || undefined,
      group2: group2 || undefined,
    };
  }, [campus, ministry, group1, group2]);
  const { access, roles } = useMyPeopleAccess(context);

  const totalCount = store.state.count;
  const items = store.state.data;

  const loadPeopleParams = useMemo(
    () => ({
      page,
      setPage,
      itemsPerPage,
      itemLimit,
      campus,
      laygroup1: group1,
      laygroup2: group2,
      memberStatus,
      search,
      tags,
    }),
    [page, itemsPerPage, campus, group1, group2, memberStatus, search, tags],
  );

  const { loading } = useLoadPeople(loadPeopleParams);

  const pagination: Pagination = useMemo(
    () => ({
      enabled: true,
      absolute: true,
      page,
      itemsPerPage,
      allowItemsPerPage: [10, 20, 50, 100],
      onPageChange: (page: number) => setPage(page),
      onItemsPerPageChange: (value: number) => setItemsPerPage(value),
    }),
    [itemsPerPage, page],
  );

  const onCampusChange = useCallback((id: string) => {
    setCampus(id);
  }, []);

  const onMinistryChange = useCallback((id: '' | MinistryTypeKey) => {
    setMinistry(id);
  }, []);

  const onMemberStatusChange = useCallback(
    (key: string | string[] | undefined) => {
      if (!Array.isArray(key)) {
        key = key ? [key] : [];
      }

      setMemberStatus(key);
    },
    [],
  );

  const [editing, setEditing] = useState<Person | null>(null);
  const [saving, setSaving] = useState(false);

  const {
    isOpen: isOpenModal,
    onOpenChange: onOpenChangeModal,
    onClose: onCloseModal,
  } = useDisclosure();

  const onSave = useCallback(
    async (item: Person) => {
      setSaving(true);
      await updatePerson({ store }, { api, auth, item });
      setSaving(false);
      onCloseModal();
    },
    [store, api, auth, onCloseModal],
  );

  const [householdMember, setHouseholdMember] = useState<Person | null>(null);
  const householdModal = useDisclosure({
    onClose: () => setHouseholdMember(null),
  });

  const { setOpen: setReminderOpen, setCurrentPerson: setReminderSubject } =
    useReminderModal();

  const { setOpen: setCommentOpen, setCurrentPerson: setCommentSubject } =
    useCommentModal();

  const { setOpen: setMilestoneOpen, setCurrentPerson: setMilestoneSubject } =
    useMilestoneModal();

  const onAction = useCallback(
    (item: Person, key: ActionKey) => {
      if (key === 'edit') {
        console.log('edit', item);
        setEditing(item);
        onOpenChangeModal();
      }

      if (key === 'view') {
        setDrawerCurrentPerson(item);
        setDrawerCurrentTab('profile');
        setDrawerIsOpen(true);
      }

      if (key === 'view-household') {
        setHouseholdMember(item);
        householdModal.onOpen();
      }

      if (key === 'comment') {
        setCommentSubject(item);
        setCommentOpen(true);
      }

      if (key === 'reminder') {
        setReminderSubject(item);
        setReminderOpen(true);
      }

      if (key === 'milestones') {
        setMilestoneSubject(item);
        setMilestoneOpen(true);
      }

      if (key === 'tags') {
        setTagsModalPerson(item);
        setTagsModalOpen(true);
      }
    },
    [
      setDrawerCurrentPerson,
      setDrawerCurrentTab,
      setDrawerIsOpen,
      onOpenChangeModal,
      householdModal,
      setReminderSubject,
      setReminderOpen,
      setCommentSubject,
      setCommentOpen,
      setMilestoneSubject,
      setMilestoneOpen,
      setTagsModalPerson,
      setTagsModalOpen,
    ],
  );

  const columns = useMemo(() => {
    const columns = makeColumns(onAction, access, ministry);
    const excludes = new Set<ColumnKey>();
    if (campus.length === 1) {
      excludes.add('campus');
    }

    if (memberStatus.length === 1) {
      excludes.add('member_status');
    }

    if (group1) {
      excludes.add('laygroup1');
    }

    if (group2) {
      excludes.add('laygroup2');
    }

    return columns.filter((col) => !excludes.has(col.key));
  }, [campus, memberStatus, group1, group2, onAction, access, ministry]);

  const checkedHashOnLoad = useRef(false);
  const [hashPersonId, setHashPersonId] = useState('');
  useEffect(() => {
    if (checkedHashOnLoad.current) {
      return;
    }

    checkedHashOnLoad.current = true;
    const hash = window.location.hash;
    if (hash?.startsWith('#/')) {
      const id = hash.substring(2);
      if (isPersonId(id)) {
        loadPerson(store, { force: true, api, auth, id, cache: false })
          .then((result) => {
            if (!result.exists) {
              console.warn('Person does not exist');
            } else if (result.exists && result.loaded) {
              setDrawerCurrentPerson(result.item!);
              setDrawerCurrentTab('comments');
              setDrawerIsOpen(true);
            }

            return;
          })
          .catch((e) => {
            console.error(e);
          });
      } else {
        console.warn('Ignoring bad person id in location hash');
      }
    }
  }, [
    store,
    api,
    auth,
    setDrawerCurrentPerson,
    setDrawerCurrentTab,
    setDrawerIsOpen,
  ]);

  useEffect(() => {
    if (!hashPersonId) {
      return;
    }

    debugger;
    const person = store.computed.peopleLookup[hashPersonId];
    setDrawerCurrentPerson(person);
    setDrawerCurrentTab('comments');
    setDrawerIsOpen(true);
    setHashPersonId('');
    // TODO clear hash when drawer closes
  }, [
    hashPersonId,
    store.computed.peopleLookup,
    setDrawerCurrentPerson,
    setDrawerCurrentTab,
    setDrawerIsOpen,
  ]);

  // TODO @vivetech implement row click here
  // const onRowClick = (item: ItemType) => {
  //   // Redirect to the person detail page
  //   // TODO(miselin): add the query params back
  //   const qs = ''; /* new URLSearchParams({
  //     ...item,
  //     ...(item?.data ? { data: JSON.stringify(item.data) } : undefined),
  //   }).toString(); */
  //   navigate(`/people/${item?.id}?${qs}`);
  // };

  return (
    <div>
      <div className='flex flex-col gap-y-2'>
        <div className='flex flex-col space-y-2 sm:flex-row sm:items-center sm:gap-x-2'>
          <CampusSelect
            startContent={<Icon icon='mdi:location' width={20} />}
            placeholder='Any Campus'
            value={campus}
            onChangeSingle={onCampusChange}
            className='basis-12 sm:basis-3/12'
            // filter={based on access policy - which ones you can view people under}
          />
          <MinistryTypeSelect
            placeholder='Select Ministry'
            value={ministry}
            onChange={onMinistryChange}
            className='basis-3/12'
          />
          {ministry === 'pastoral_care' && (
            <GroupSelect
              type={'laygroup1'}
              startContent={
                <Icon icon={getGroupProperty('laygroup1', 'icon')} width={20} />
              }
              placeholder={getGroupAlias('laygroup1', 'type')}
              campus={campus}
              value={group1}
              onChangeSingle={setGroup1}
              className='basis-3/12'
              // filter={based on access policy which ones you can view people under}
            />
          )}
          {ministry === 'pastoral_care' && (
            <GroupSelect
              type={'laygroup2'}
              placeholder={getGroupAlias('laygroup2', 'type')}
              startContent={
                <Icon icon={getGroupProperty('laygroup2', 'icon')} width={20} />
              }
              campus={campus}
              parent={group1}
              value={group2}
              onChangeSingle={setGroup2}
              className='basis-3/12'
              // filter={based on access policy which ones you can view people under}
            />
          )}
        </div>
        <div className='flex flex-col space-y-2 sm:flex-row sm:items-center sm:gap-x-2'>
          <MemberStatusSelect
            className='basis-1/4'
            startContent={<Icon icon='mdi:wallet-membership' width={20} />}
            placeholder='Member Status'
            selected={filters.memberStatus}
            onChange={onMemberStatusChange}
          />
          <TagSelect
            value={tags[0] || undefined}
            placeholder='Tags'
            className='basis-1/4'
            startContent={<Icon icon='mdi:tag-outline' width={20} />}
            onSingleChange={(value) => setTags(value ? [value] : [])}
          />
          <PeopleSearch
            className='basis-2/4'
            placeholder='Search by name, email or phone'
            startContent={<Icon icon='mdi:search' width={20} />}
            onChange={setSearch}
          />
        </div>
      </div>
      <LoadingBar loading={loading} />
      <DataTable
        aria-label='People'
        columns={columns}
        items={items}
        count={totalCount}
        pagination={pagination}
        loading={loading}
        className='text-gray-800'
        empty={{
          icon: 'mdi:account',
          description: 'No people found',
        }}
      />
      <PersonModal
        isOpen={isOpenModal}
        onOpenChange={onOpenChangeModal}
        target={editing}
        onSave={onSave}
        saving={saving}
        fields={[
          'firstname',
          'lastname',
          'preferredname',
          'fullname',
          'email',
          'phone',
          'address',
          'gender',
          'birthdate',
          'campus_id',
          'member_status',
          'laygroup1',
          'laygroup2',
        ]}
      />
      <HouseholdModal person={householdMember} disclosure={householdModal} />
      <Debug value={{ access, roles, context, filters }} />
    </div>
  );
}

People.whyDidYouRender = true;
